import { ReactNode } from 'react';
import CakeOutlinedIcon from '@mui/icons-material/CakeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FlightTakeoffOutlinedIcon from '@mui/icons-material/FlightTakeoffOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined';
import { NotificationType } from '../types/notification';

/** Real-time notification system (Phase 51) — single source of truth for
 * how each NotificationType renders (icon + priority/colour). Shared by the
 * bell dropdown, the toast host, and the full Notifications page so the
 * three surfaces can never visibly disagree with each other. Where a click
 * lands is resolved by the server (see useOpenNotification). Priority/colour is derived purely from `type`
 * — deliberately not a stored column — using the app's own existing MUI
 * theme tokens (success.main/warning.main/error.main/info.main), never an
 * invented palette (§14). */
export type NotificationPriority = 'info' | 'success' | 'warning' | 'urgent';

interface NotificationMeta {
  icon: (props: { fontSize?: 'small' | 'medium' }) => ReactNode;
  priority: NotificationPriority;
}

const iconOf = (Icon: typeof CakeOutlinedIcon) => (props: { fontSize?: 'small' | 'medium' }) => <Icon {...props} />;

export const NOTIFICATION_META: Record<NotificationType, NotificationMeta> = {
  BIRTHDAY: { icon: iconOf(CakeOutlinedIcon), priority: 'info' },
  GENERAL: { icon: iconOf(NotificationsOutlinedIcon), priority: 'info' },
  TASK_REMINDER: { icon: iconOf(EventAvailableOutlinedIcon), priority: 'info' },
  LEAD_PENDING_APPROVAL: { icon: iconOf(ContactsOutlinedIcon), priority: 'info' },
  LEAD_ASSIGNED: { icon: iconOf(ContactsOutlinedIcon), priority: 'info' },
  LEAD_APPROVED: { icon: iconOf(CheckCircleOutlinedIcon), priority: 'success' },
  LEAD_REJECTED: { icon: iconOf(CancelOutlinedIcon), priority: 'urgent' },
  PAYMENT_REMINDER: { icon: iconOf(PaymentsOutlinedIcon), priority: 'warning' },
  PAYMENT_VERIFICATION: { icon: iconOf(PaymentsOutlinedIcon), priority: 'warning' },
  PAYMENT_APPROVED: { icon: iconOf(PaymentsOutlinedIcon), priority: 'success' },
  PAYMENT_REJECTED: { icon: iconOf(PaymentsOutlinedIcon), priority: 'urgent' },
  PAYMENT_EVIDENCE_REQUESTED: { icon: iconOf(PaymentsOutlinedIcon), priority: 'warning' },
  DOCUMENT_EXPIRY: { icon: iconOf(DescriptionOutlinedIcon), priority: 'warning' },
  DOCUMENT_UPLOADED: { icon: iconOf(DescriptionOutlinedIcon), priority: 'warning' },
  DOCUMENT_REJECTED: { icon: iconOf(DescriptionOutlinedIcon), priority: 'urgent' },
  VISA_FOLLOWUP: { icon: iconOf(FlightTakeoffOutlinedIcon), priority: 'warning' },
  STUDENT_ADMISSION_ELIGIBLE: { icon: iconOf(SchoolOutlinedIcon), priority: 'success' },
  BATCH_ASSIGNED: { icon: iconOf(GroupsOutlinedIcon), priority: 'info' },
  DEPARTURE_REMINDER: { icon: iconOf(FlightTakeoffOutlinedIcon), priority: 'warning' },
  // Student-Wise Payment Customization (Phase 52)
  FEE_CHANGE_PENDING: { icon: iconOf(RequestQuoteOutlinedIcon), priority: 'warning' },
  FEE_CHANGE_APPROVED: { icon: iconOf(RequestQuoteOutlinedIcon), priority: 'success' },
  FEE_CHANGE_REJECTED: { icon: iconOf(RequestQuoteOutlinedIcon), priority: 'urgent' },
  AGREEMENT_UPDATE_REQUIRED: { icon: iconOf(WarningAmberOutlinedIcon), priority: 'warning' },
  // Student Portal (Phase 67) — a Counselor's own copy of "a student
  // submitted a change request, go review it" (mirrors FEE_CHANGE_PENDING's
  // 'warning' treatment: something needs this recipient's review).
  CHANGE_REQUEST_SUBMITTED: { icon: iconOf(EditNoteOutlinedIcon), priority: 'warning' },
  // SSCL tax / exchange-rate payment overhaul (Phase 79) — the exchange-
  // rate fetcher has missed its schedule or is failing; 'warning' (not
  // 'urgent') since payments against taxable items still work, they just
  // can't compute an LKR figure until a fresh rate arrives.
  EXCHANGE_RATE_STALE: { icon: iconOf(CurrencyExchangeOutlinedIcon), priority: 'warning' },
  // Intake/batch transfers (Phase 94): a request awaiting a Super Admin,
  // then its outcome for the requester.
  INTAKE_TRANSFER_PENDING: { icon: iconOf(SwapHorizOutlinedIcon), priority: 'warning' },
  INTAKE_TRANSFER_APPROVED: { icon: iconOf(SwapHorizOutlinedIcon), priority: 'success' },
  INTAKE_TRANSFER_REJECTED: { icon: iconOf(SwapHorizOutlinedIcon), priority: 'urgent' },
};

/** Blank-page bug (2026-09-29) — `notification.type` is only as
 * trustworthy as this map's own upkeep: the backend's `NotificationType`
 * enum and this frontend map are maintained separately (as just proven by
 * `CHANGE_REQUEST_SUBMITTED` existing in real, already-generated
 * production data since Phase 67 without ever being added here), so a
 * lookup miss is a real, recurring category of data this app must
 * render, not a hypothetical. This is the ONLY place that ever indexes
 * `NOTIFICATION_META` directly — every consumer goes through this
 * instead, so "notification type not yet styled" has one exact, sensible
 * meaning (a generic bell at 'info' priority) rather than each call site
 * separately deciding what `undefined` should mean. */
export const getNotificationMeta = (type: NotificationType): NotificationMeta =>
  NOTIFICATION_META[type] ?? { icon: iconOf(NotificationsOutlinedIcon), priority: 'info' };

/** Maps an MUI priority key to the theme's own palette token path, so
 * every consumer reads colour the same way instead of hard-coding hex
 * values (§14: "use existing design tokens rather than introducing
 * random colours"). */
export const PRIORITY_COLOR: Record<NotificationPriority, string> = {
  info: 'info.main',
  success: 'success.main',
  warning: 'warning.main',
  urgent: 'error.main',
};
